import {getPortalTypeFromEnvClient} from "@utils/get-portal-type-from-env.client";
import type {ICurrencyConversionService} from "@studyportals/portal-localized-content/CurrencyConversion/I-CurrencyConversionService";
import {Pagination} from "@domain/enums/Pagination";
import {computed, ref, watch} from "vue";
import {defineStore} from "pinia";
import type {Ref} from "vue";
import {useRoute, useRouter} from "vue-router";
import {SearchTabText} from "@domain/enums/SearchTabText";
import type {ITuitionFeeRange} from "@app/interfaces/dtos/ITuitionFeeRange";
import {RouteMatchResult} from "@domain/interfaces/mappers/RouteMatchResult";
import { FilterType, OmniSearchFiltersClearedEvent, OmniSearchReadyEvent } from "@studyportals/omnisearch-interfaces";
import type IExtendedSearchQueryFilter from "@app/interfaces/dtos/IExtendedSearchQueryFilter";
import {FilterProcessingService} from "@app/routing/FilterProcessingService";
import {useSearchAPI} from "@composables/useSearchAPI";
import { areasAll } from "@studyportals/static-domain-data/areas/areas";
import { useCookie } from "nuxt/app";
import { IPersonalizationData } from "@domain/interfaces/dtos/IPersonalizationData";
import { TrackingService } from "@domain/services/TrackingService";
import { getPortalTypeDefinitionClient } from "@app/utils/get-portal-type-def.client";
import { getPortalTypeDefinitionServer } from "@app/utils/get-portal-type-def.server";
import { getPortalPathFromType } from "@app/utils/portal-detector";
import { PortalType } from "@studyportals/domain-client";
import { OmniSearchApplication } from "@app/application";
import { useEventAggregationService } from "@composables/useEventAggregationService";
import { OmniSearchFiltersUpdatedEvent } from "../../../omnisearch-interfaces";
import { AttendanceFilterValue } from "@studyportals/search-api-interface";
import { isFilterTypeAllowedForSearchType } from "@domain/mappers/AvailableFilterMappers";
import { SearchType } from "@domain/enums/SearchType";

const VISUALLY_HIDDEN_FILTERS_TYPE = [FilterType.CAMPAIGN_TYPE, FilterType.SORTING];

export const useSearchStore = defineStore('search', () => {
	const application = new OmniSearchApplication();
	const eventAggregationService = useEventAggregationService();

	const filtersProcessingService = FilterProcessingService.getInstance();

	const router = useRouter();
	const currentRoute = useRoute();
	const { value: personalizationDataCookie } = useCookie("Personalization-Data");
	const personalizationData = personalizationDataCookie as  IPersonalizationData;
	const portalDefinition = import.meta.server ? getPortalTypeDefinitionServer() : getPortalTypeDefinitionClient();


	const filtersSelection = ref<IExtendedSearchQueryFilter[]>([]);
	const externalServicesLoaded: Ref<boolean> = ref(false);
	const portalType: Ref<string> = ref(getPortalTypeFromEnvClient());
	const currencyConversionService: Ref<ICurrencyConversionService> = ref({} as ICurrencyConversionService);
	const isProfileQuestionnairesReady: Ref<boolean> = ref(false);
	const tuitionFeeRange: Ref<ITuitionFeeRange | null> = ref(null);
	const pageTitle: Ref<string | null> = ref(null);

	const userNationalityIso: Ref<string> = ref(personalizationData?.nationality_country_iso || "NL");
	const userResidenceCountryId: Ref<number> = ref(1);

	const userCurrency: Ref<string> = ref(personalizationData?.currency || "EUR");
	const searchType: Ref<SearchType> = ref(filtersProcessingService.getSearchType(currentRoute) || SearchType.PROGRAMME_SEARCH);
	const trackingService = TrackingService.getInstance();

	const currentPage : Ref<number> = ref(
		router.currentRoute.value.query.page
			? parseInt(router.currentRoute.value.query.page as string, 10)
			: Pagination.DEFAULT_PAGE
	);

	const offset = computed(() => (currentPage.value - 1) * Pagination.PAGE_SIZE);
	const sortBy = computed(() => router?.currentRoute.value.query.sorting as | string | undefined);
	const nonExtendedFiltersSelection = computed(() => filtersSelection.value.map(
		(filter) => ({
			type: filter.type,
			isIndexable: filter.isIndexable,
			values: filter.values.map((value: { id: string }) => value.id),
		})
	));

	const areaIsoById = new Map<string, string>();
	for (const area of areasAll) {
		const { countryIso } = area;

		areaIsoById.set(area.id.toString(), countryIso);
	}

	const fetchResult = useSearchAPI(
		nonExtendedFiltersSelection,
		sortBy,
		offset
	);
	const {data: pageData, execute: executeSearch, status } = fetchResult;
	const loading = computed(() => {
		return status.value === 'pending';
	});

	const changeableFiltersSelection = computed(() => {
		return filtersSelection.value
			.filter((filter) => {
				if(VISUALLY_HIDDEN_FILTERS_TYPE.includes(filter.type)) return false;
				if (!isFilterTypeAllowedForSearchType(filter.type, searchType.value)) return false;

				return filter.type !== FilterType.EDUCATION_LEVEL || portalType.value === PortalType.DISTANCE_LEARNING.toString();
			})
			.map((filter) => {
				const mapper = filtersProcessingService.mappers.find(m => m.filterType === filter.type);
				if (!mapper) return filter;

				const nonImplicitValues = filter.values.filter(
					(v) => !mapper.isImplicitForPortalType(v.id, portalType.value as PortalType)
				);

				if (nonImplicitValues.length === 0) return null;
				return { ...filter, values: nonImplicitValues };
			})
			.filter((filter): filter is IExtendedSearchQueryFilter => filter !== null);
	});

	if (import.meta.client) {
		watch(
			filtersSelection,
			(newFilters, oldFilters) => {
				void trackingService.trackFilterChanges(newFilters, oldFilters);
			},
			{ deep: true }
		);

		let applicationInitialized = false;
		watch(filtersSelection, () => {
			if (!applicationInitialized) {
				const event = new OmniSearchReadyEvent(application);
				eventAggregationService?.publishTo(event.eventType, event);
				applicationInitialized = true;
			} else {
				const event = new OmniSearchFiltersUpdatedEvent();
				eventAggregationService?.publishTo(event.eventType, event);
			}
		});
	}


	const trackSiteSearch = (): void => {
		if (!pageData.value) return;

		trackingService.trackSearchFromFilters(
			filtersSelection.value,
			pageData.value.totalResultsCount
		);
	};

	const loadPageData = async (pageNumber?: number): Promise<void> => {
		try {
			currentPage.value = pageNumber || currentPage.value;
			searchType.value = filtersProcessingService.getSearchType(currentRoute);
			filtersSelection.value = await filtersProcessingService.processRoute(currentRoute);
			filtersSelection.value = filtersProcessingService.ensureImplicitFilters(filtersSelection.value, portalType.value as PortalType);

			if (searchType.value === SearchType.UNIVERSITY_SEARCH) {
				pageData.value = null;
				return Promise.resolve(undefined);
			}

			const searchResult = await executeSearch();

			trackSiteSearch();
			return searchResult;
		} catch (error: unknown) {
			console.error("Error loading page data:", error);

			const parsedError = error instanceof Error? error : new Error("Failed to load page data");
			return Promise.reject(parsedError);
		}
	};

	const setExternalServicesAsLoaded = (): void => {
		externalServicesLoaded.value = false;
		externalServicesLoaded.value = true;
	};

	const setSearchTab = (searchTabText: SearchTabText): void => {
		void (async (): Promise<void> => {
			switch (searchTabText) {
			case SearchTabText.PROGRAMMES:
				searchType.value = SearchType.PROGRAMME_SEARCH;
				if (!router.currentRoute.value.path.includes('/programmes/')) {
					await navigateToSearchTypeRoute(SearchType.PROGRAMME_SEARCH);
				}
				break;
			case SearchTabText.UNIVERSITIES:
				searchType.value = SearchType.UNIVERSITY_SEARCH;
				if (!router.currentRoute.value.path.includes('/universities/')) {
					await navigateToSearchTypeRoute(SearchType.UNIVERSITY_SEARCH);
				}
				break;
			case SearchTabText.SCHOLARSHIPS:
				searchType.value = SearchType.SCHOLARSHIP_SEARCH;
				if (!router.currentRoute.value.path.includes('/scholarships/')) {
					await navigateToSearchTypeRoute(SearchType.SCHOLARSHIP_SEARCH);
				}
				break;
			}
		})();
	};

	const navigateToSearchTypeRoute = async (targetSearchType: SearchType): Promise<void> => {
		const routeMatch = await filtersProcessingService.setSearchTypeRedirection(router, targetSearchType);
		await router.push(routeMatch);
		await loadPageData();
	};

	const setPageTitle = (title: string): void => {
		pageTitle.value = title;
	};

	const processCurrencyOrNationalityChange = async (
		newUserNationalityIso?: string,
		newUserCurrency?: string
	): Promise<void> => {
		let changed = false;

		if (newUserCurrency && newUserCurrency !== userCurrency.value) {
			userCurrency.value = newUserCurrency;
			changed = true;
		}

		if (newUserNationalityIso && newUserNationalityIso !== userNationalityIso.value) {
			userNationalityIso.value = newUserNationalityIso;
			changed = true;
		}

		if (changed) {
			await loadPageData();
		}
	};

	const setPage = async (pageNumber: number): Promise<void> => {

		if(isNaN(pageNumber) || pageNumber < 1  ) {
			pageNumber = Pagination.DEFAULT_PAGE;
		}
		const queryWithoutPage = Object.fromEntries(
			Object.entries(router.currentRoute.value.query).filter(([key]) => key !== 'page')
		);

		await router.replace({
			query: pageNumber === 1
				? queryWithoutPage
				: {
					...queryWithoutPage,
					page: pageNumber.toString(),
				},
		});

		await loadPageData(pageNumber);
	};

	const setProfileQuestionnairesReady = (ready: boolean): void => {
		isProfileQuestionnairesReady.value = ready;
	};

	const setTuitionFeeRange = (range: ITuitionFeeRange): void => {
		tuitionFeeRange.value = range;
	};

	const setRouteFromFilters = async (): Promise<void> => {
		filtersSelection.value = filtersProcessingService.ensureImplicitFilters(filtersSelection.value, portalType.value as PortalType);
		const mappedRoute: RouteMatchResult | undefined =
			filtersProcessingService.processFilters(router, filtersSelection.value);

		await router.replace({
			name: mappedRoute.name,
			params: mappedRoute.params,
			query: mappedRoute.query,
			hash: mappedRoute.hash,
		});

		currentPage.value = Pagination.DEFAULT_PAGE;
	};

	const cleanFilters = async (): Promise<void> => {
		const implicitFilters: IExtendedSearchQueryFilter[] = filtersSelection.value
			.map((f) => {
				const mapper = filtersProcessingService.mappers.find(m => m.filterType === f.type);
				if (!mapper) return null;

				const implicitValues = f.values.filter(
					(v) => mapper.isImplicitForPortalType(v.id, portalType.value as PortalType)
				);

				if (implicitValues.length === 0) return null;
				return { ...f, values: implicitValues };
			})
			.filter((f): f is IExtendedSearchQueryFilter => f !== null);

		filtersSelection.value = implicitFilters;
		const portalPath = getPortalPathFromType(portalType.value as PortalType);
		const extraParams = (portalType.value === PortalType.DISTANCE_LEARNING.toString()) ?
			{ [FilterType.ATTENDANCE]: AttendanceFilterValue.ONLINE } :
			{};

		await router.push({
			name: portalDefinition,
			params: { [FilterType.EDUCATION_LEVEL]: portalPath, ...extraParams},
			query: { ...extraParams },
		});
		currentPage.value = Pagination.DEFAULT_PAGE;
		await loadPageData();

		eventAggregationService?.publishTo(OmniSearchFiltersClearedEvent.EventType, new OmniSearchFiltersClearedEvent());
	};

	const removeFilterValueByType = async (filterType: FilterType, filterValue: string): Promise<void> => {
		const filters = filtersSelection.value;
		const filterTypeTarget = filters.find(
			(f) => f.type === filterType
		);
		if (!filterTypeTarget) return;

		let nextFilters: IExtendedSearchQueryFilter[] = [...filters];
		if (filterTypeTarget.values.length === 1) {
			nextFilters = nextFilters.filter(
				(f) => f.type !== filterType
			);
		} else {
			const updatedSelection: IExtendedSearchQueryFilter = {
				...filterTypeTarget,
				values: filterTypeTarget.values.filter((v) => v.id !== filterValue)
			};
			nextFilters = nextFilters.map((filter) => filter.type === filterType ? updatedSelection : filter);
		}

		if (filterType === FilterType.COUNTRY) {
			const remainingFilters: IExtendedSearchQueryFilter[] = [];
			for (const filter of nextFilters) {
				if (filter.type !== FilterType.AREA) {
					remainingFilters.push(filter);
					continue;
				}

				const values = filter.values.filter((v) => {
					const iso = areaIsoById.get(String(v.id));
					return iso !== String(filterValue);
				});

				if (values.length) remainingFilters.push({ ...filter, values });
			}
			nextFilters = remainingFilters;
		}

		filtersSelection.value = nextFilters;

		await setRouteAndLoadData();
	};

	const removeFilterByType = async (
		filterTypes: FilterType[]
	): Promise<void> => {
		const filters = filtersSelection.value;
		const removedCountryIsos = filterTypes.includes(FilterType.COUNTRY)
			? new Set(
				filters
					.find((f) => f.type === FilterType.COUNTRY)
					?.values.filter((v) => {
						const mapper = filtersProcessingService.mappers.find(m => m.filterType === FilterType.COUNTRY);
						return !mapper?.isImplicitForPortalType(String(v.id), portalType.value as PortalType);
					})
					.map((v) => String(v.id)) ?? []
			)
			: new Set<string>();

		let nextFilters: IExtendedSearchQueryFilter[] = filters
			.map((f) => {
				if (!filterTypes.includes(f.type)) return f;

				const mapper = filtersProcessingService.mappers.find(m => m.filterType === f.type);
				if (!mapper) return null;

				const implicitValues = f.values.filter(
					(v) => mapper.isImplicitForPortalType(v.id, portalType.value as PortalType)
				);

				if (implicitValues.length === 0) return null;
				return { ...f, values: implicitValues };
			})
			.filter((f): f is IExtendedSearchQueryFilter => f !== null);

		if (removedCountryIsos.size > 0 && !filterTypes.includes(FilterType.AREA)) {
			const remainingFilters: IExtendedSearchQueryFilter[] = [];
			for (const filter of nextFilters) {
				if (filter.type !== FilterType.AREA) {
					remainingFilters.push(filter);
					continue;
				}

				const values = filter.values.filter((v) => {
					const iso = areaIsoById.get(String(v.id));
					return iso ? !removedCountryIsos.has(iso) : true;
				});

				if (values.length) remainingFilters.push({ ...filter, values });
			}

			nextFilters = remainingFilters;
		}

		filtersSelection.value = nextFilters;

		await setRouteAndLoadData();
	};

	const setRouteAndLoadData = async (): Promise<void> => {
		await setRouteFromFilters();
		await loadPageData();
	};

	return {
		// Data,
		pageData,
		filtersSelection,
		loading,
		externalServicesLoaded,
		portalType,
		currencyConversionService,
		isProfileQuestionnairesReady,
		tuitionFeeRange,
		userNationalityIso,
		userResidenceCountryId,
		userCurrency,
		searchType,
		currentPage,
		offset,
		pageTitle,

		// Computed
		nonExtendedFiltersSelection,
		changeableFiltersSelection,

		// Actions
		executeSearch,
		loadPageData,
		trackSiteSearch,
		setExternalServicesAsLoaded,
		setSearchTab,
		processCurrencyOrNationalityChange,
		setPage,
		setProfileQuestionnairesReady,
		setTuitionFeeRange,
		setRouteFromFilters,
		cleanFilters,
		removeFilterValueByType,
		removeFilterByType,
		setRouteAndLoadData,
		setPageTitle

	};
})
